import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Euro, TrendingUp, Calendar, ShoppingBag, Wallet, Loader2, Download, Target, Bell } from "lucide-react";
import { API, fmtDate } from "./utils";
import { T } from "@/locales/tr";

const fmtEur = (n) => `${(Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

// Configurations stables des charts — déclarées au module-level pour éviter les re-renders inutiles
const CHART_MARGIN = { top: 8, right: 8, bottom: 0, left: 0 };
const CHART_AXIS_TICK = { fontSize: 10, fill: "#5a5147" };
const CHART_CURSOR = { fill: "rgba(212,175,55,0.08)" };
const CHART_TOOLTIP_STYLE = { background: "#fff", border: "1px solid #e8e0cf", borderRadius: 8, fontSize: 12 };
const BAR_RADIUS = [4, 4, 0, 0];

const monthOptions = () => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
};

export default function RevenueTab({ pwd }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalValue, setGoalValue] = useState("");
  const [alertEmail, setAlertEmail] = useState("");
  const [exportMonth, setExportMonth] = useState(monthOptions()[0]);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/revenue/stats`, { headers: { "X-Admin-Password": pwd } });
      setData(data);
      setGoalValue(String(data.goal?.monthly_target_eur || 500));
      setAlertEmail(data.goal?.alert_email || "");
    } catch { toast.error(T("Échec chargement revenus.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveGoal = async () => {
    const v = Number(goalValue);
    if (!Number.isFinite(v) || v < 0) { toast.error("Objectif invalide"); return; }
    try {
      await axios.post(`${API}/admin/revenue/goal`, { monthly_target_eur: v, alert_email: alertEmail || null },
        { headers: { "X-Admin-Password": pwd } });
      toast.success(T("Objectif mis à jour 🌹"));
      setEditingGoal(false); load();
    } catch { toast.error(T("Échec sauvegarde.")); }
  };

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/revenue/export.csv`, {
        headers: { "X-Admin-Password": pwd }, params: { month: exportMonth }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `papicoo-revenus-${exportMonth}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`CSV ${exportMonth} téléchargé.`);
    } catch (e) { toast.error(e?.response?.data?.detail || T("Export échoué.")); }
    finally { setExporting(false); }
  };

  const triggerAlertCheck = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/revenue/alerts/check`, null, { headers: { "X-Admin-Password": pwd } });
      toast.success(`Check OK. ${data.recent_alerts?.length || 0} alerte(s) envoyée(s) à vie.`);
    } catch { toast.error(T("Échec check alertes.")); }
  };

  if (loading) return (
    <div data-testid="admin-revenue-tab" className="py-12 text-center text-[color:var(--papicoo-muted)] italic">
      <Loader2 className="animate-spin inline-block mr-2" /> {T("Chargement des revenus…")}
    </div>
  );
  if (!data) return null;

  const { totals, top_services, daily_30d, recent, goal } = data;
  const pct = Math.min(100, goal?.progress_pct || 0);

  return (
    <div data-testid="admin-revenue-tab" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-2xl text-[color:var(--papicoo-ink)]">{T("Revenus · pilotage temps réel")}</h2>
          <p className="text-[11px] text-[color:var(--papicoo-muted)] italic">Snapshot {fmtDate(data.as_of)} · agrégation Stripe LIVE</p>
        </div>
        <button onClick={load} className="text-xs px-3 py-1.5 rounded-full bg-white border border-[color:var(--papicoo-border)] hover:bg-[color:var(--papicoo-sand-deep)]/40">{T("↻ Rafraîchir")}</button>
      </div>

      {/* 4 KPI cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi data-testid="kpi-lifetime" Icon={Euro} label={T("Total à vie")} value={fmtEur(totals.lifetime_amount)} sub={`${totals.lifetime_count} transactions`} color="text-[color:var(--papicoo-gold)]" />
        <Kpi data-testid="kpi-month" Icon={Calendar} label={T("Ce mois-ci")} value={fmtEur(totals.month_amount)} sub={`${totals.month_count} ventes`} color="text-emerald-700" />
        <Kpi data-testid="kpi-30d" Icon={TrendingUp} label={T("30 derniers jours")} value={fmtEur(totals.last30d_amount)} sub={`${totals.last30d_count} ventes`} color="text-[color:var(--papicoo-accent)]" />
        <Kpi data-testid="kpi-basket" Icon={Wallet} label={T("Panier moyen")} value={fmtEur(totals.average_basket)} sub="par transaction" color="text-[color:var(--papicoo-ink)]" />
      </div>

      {/* Goal + Export + Alertes */}
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-3">
        <div data-testid="revenue-goal-card" className={`papicoo-card p-5 ${goal?.reached ? "bg-emerald-50 border-l-4 border-emerald-500" : "bg-gradient-to-br from-[color:var(--papicoo-gold)]/8 to-transparent"}`}>
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-accent)]">
              <Target size={13} /> {T("Objectif mensuel")}
            </div>
            {!editingGoal && (
              <button onClick={() => setEditingGoal(true)} className="text-xs underline text-[color:var(--papicoo-muted)] hover:text-[color:var(--papicoo-ink)]">{T("Modifier")}</button>
            )}
          </div>
          {editingGoal ? (
            <div className="flex flex-wrap gap-2 items-center">
              <input data-testid="goal-input" type="number" value={goalValue} onChange={(e) => setGoalValue(e.target.value)} className="px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm w-32" placeholder="500" />
              <span className="text-sm">{T("€ / mois · alerte email :")}</span>
              <input data-testid="goal-email" type="email" value={alertEmail} onChange={(e) => setAlertEmail(e.target.value)} className="px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm flex-1 min-w-[200px]" placeholder={T("papicoo@papicoo.fr")} />
              <button data-testid="goal-save" onClick={saveGoal} className="px-4 py-2 rounded-full papicoo-btn-primary text-sm">{T("Enregistrer")}</button>
              <button onClick={() => setEditingGoal(false)} className="px-3 py-2 text-xs text-[color:var(--papicoo-muted)]">{T("Annuler")}</button>
            </div>
          ) : (
            <>
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <div>
                  <span className="font-display text-3xl text-[color:var(--papicoo-ink)] tabular-nums">{fmtEur(totals.month_amount)}</span>
                  <span className="text-sm text-[color:var(--papicoo-muted)]"> / {fmtEur(goal?.monthly_target_eur || 0)}</span>
                </div>
                <span className={`font-display text-2xl tabular-nums ${goal?.reached ? "text-emerald-600" : "text-[color:var(--papicoo-gold)]"}`}>{(goal?.progress_pct || 0).toFixed(1)}%</span>
              </div>
              <div className="h-3 rounded-full bg-[color:var(--papicoo-sand-deep)] overflow-hidden">
                <div className={`h-full rounded-full transition-all ${goal?.reached ? "bg-emerald-500" : "bg-gradient-to-r from-[color:var(--papicoo-gold)] to-[color:var(--papicoo-accent)]"}`} style={{ width: `${pct}%` }} />
              </div>
              <div className="text-[11px] text-[color:var(--papicoo-muted)] mt-2 italic">
                {goal?.reached ? "🌹 Objectif atteint ce mois. Force tranquille." : `Paliers d'alerte email automatiques : 100€ · 250€ · 500€ · 1000€ · 2000€ · 5000€ → ${goal?.alert_email}`}
              </div>
            </>
          )}
        </div>

        <div className="papicoo-card p-5 space-y-3">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-accent)]">
            <Download size={13} /> {T("Export comptabilité")}
          </div>
          <div className="flex flex-wrap gap-2">
            <select data-testid="csv-month" value={exportMonth} onChange={(e) => setExportMonth(e.target.value)} className="px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm">
              {monthOptions().map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <button data-testid="csv-download" onClick={downloadCsv} disabled={exporting} className="px-4 py-2 rounded-full papicoo-btn-gold text-sm inline-flex items-center gap-2 disabled:opacity-60">
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              CSV
            </button>
          </div>
          <button data-testid="alerts-check" onClick={triggerAlertCheck} className="w-full text-xs px-3 py-2 rounded-full bg-white border border-[color:var(--papicoo-border)] hover:bg-[color:var(--papicoo-sand-deep)]/40 inline-flex items-center justify-center gap-1.5">
            <Bell size={12} /> {T("Tester les alertes paliers")}
          </button>
        </div>
      </div>

      {/* Daily chart */}
      <div className="papicoo-card p-5">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)] mb-3">{T("Revenus quotidiens — 30 derniers jours (€)")}</div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={daily_30d} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8e0cf" />
            <XAxis dataKey="date" tick={CHART_AXIS_TICK} tickFormatter={(d) => d.slice(5)} />
            <YAxis tick={CHART_AXIS_TICK} />
            <Tooltip
              cursor={CHART_CURSOR}
              contentStyle={CHART_TOOLTIP_STYLE}
              formatter={(v, k) => k === "amount" ? [fmtEur(v), "Revenu"] : [v, "Ventes"]}
            />
            <Bar dataKey="amount" fill="#D4AF37" radius={BAR_RADIUS} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Top services */}
        <div className="papicoo-card p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)] mb-3 inline-flex items-center gap-2">
            <ShoppingBag size={12} /> {T("Top services")}
          </div>
          {top_services.length === 0 ? (
            <div className="italic text-sm text-[color:var(--papicoo-muted)] py-4">{T("Aucune vente encore. Une fois Stripe Verify activé, les revenus apparaissent ici en temps réel.")}</div>
          ) : (
            <div className="space-y-2">
              {top_services.map((s, i) => {
                const pct = totals.lifetime_amount > 0 ? (s.amount / totals.lifetime_amount * 100) : 0;
                return (
                  <div key={s.kind} data-testid={`top-${s.kind}`} className="text-sm">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium text-[color:var(--papicoo-ink)] truncate">{i + 1}. {T(s.label)}</span>
                      <span className="text-[color:var(--papicoo-gold)] font-medium shrink-0">{fmtEur(s.amount)}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-1.5 bg-[color:var(--papicoo-sand-deep)] rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[color:var(--papicoo-gold)] to-[color:var(--papicoo-accent)] rounded-full" style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                      <span className="text-[10px] text-[color:var(--papicoo-muted)] tabular-nums w-20 text-right">{s.count} ventes</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Dernières transactions */}
        <div className="papicoo-card p-0 overflow-hidden">
          <div className="p-4 text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)] border-b border-[color:var(--papicoo-border)]">{T("50 dernières transactions")}</div>
          <div className="overflow-y-auto max-h-[420px]">
            <table className="w-full text-sm">
              <thead className="bg-[color:var(--papicoo-sand-deep)]/40 text-[10px] uppercase tracking-[0.15em] text-[color:var(--papicoo-muted)] sticky top-0">
                <tr><th className="text-left p-2">{T("Service")}</th><th className="text-left p-2">{T("Montant")}</th><th className="text-left p-2">{T("Date")}</th></tr>
              </thead>
              <tbody>
                {recent.length === 0 ? (
                  <tr><td colSpan={3} className="text-center italic text-[color:var(--papicoo-muted)] py-6">{T("Aucune transaction.")}</td></tr>
                ) : recent.map((r) => (
                  <tr key={r.session_id} className="border-t border-[color:var(--papicoo-border)] hover:bg-[color:var(--papicoo-sand-deep)]/20">
                    <td className="p-2">
                      <div className="font-medium text-xs">{T(r.label)}</div>
                      <div className="text-[10px] text-[color:var(--papicoo-muted)] truncate max-w-[160px]" title={r.buyer_email || r.email || ""}>{r.buyer_email || r.email || ""}</div>
                    </td>
                    <td className="p-2 text-[color:var(--papicoo-gold)] font-medium tabular-nums whitespace-nowrap">{fmtEur(r.amount_eur)}</td>
                    <td className="p-2 text-[10px] text-[color:var(--papicoo-muted)] whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ Icon, label, value, sub, color, ...rest }) {
  return (
    <div {...rest} className="papicoo-card p-4 bg-white">
      <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] ${color || "text-[color:var(--papicoo-muted)]"}`}>
        <Icon size={11} /> {label}
      </div>
      <div className="font-display text-2xl text-[color:var(--papicoo-ink)] mt-1 tabular-nums">{value}</div>
      <div className="text-[11px] text-[color:var(--papicoo-muted)] mt-0.5">{sub}</div>
    </div>
  );
}
